import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLogin2, IconSun, IconWifi, IconWifiOff } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  DrawSpreadCardInput,
  GuestSessionCredentials,
  MoveSpreadCardInput,
  SpreadSessionView,
} from '@creativitas/shared';
import { APP_NAME } from '@creativitas/shared';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { SwitchGuestButton } from '@/components/domain/SwitchGuestButton';
import { toUserMessage } from '@/lib/api';
import {
  forgetGuestCredentials,
  isRevokedGuestAccess,
  loadGuestCredentials,
  saveGuestCredentials,
} from '@/lib/guest-credentials';
import {
  drawGuestSpreadCard,
  fetchGuestFreeformDecks,
  fetchGuestSpreadSession,
  fetchSpreadInvitation,
  joinSpreadSession,
  moveGuestSpreadCard,
  removeGuestSpreadCard,
} from './api';
import { FreeformSpread } from './FreeformSpread';
import { applySession, keepNewestSession } from './session-cache';
import { SpreadCardsPanel } from './SpreadCardsPanel';
import { PresetSpread } from './PresetSpread';
import { SessionParticipants } from './SessionParticipants';
import { useSessionRealtime } from './useSessionRealtime';
import { GuestGameSession } from '@/features/games/GuestGameSession';

export function JoinSpreadSessionPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  /* Кем гость был в этой сессии — помнит браузер, см. `guest-credentials`. */
  const [credentials, setCredentials] = useState<GuestSessionCredentials | null>(() =>
    loadGuestCredentials(token),
  );
  /* Почему снова форма имени, если прежний участник больше не действует. */
  const [notice, setNotice] = useState<string | null>(null);
  const invitation = useQuery({
    queryKey: ['spread-invitation', token],
    queryFn: () => fetchSpreadInvitation(token),
    enabled: Boolean(token),
    retry: false,
  });
  const sessionKey = ['guest-spread-session', credentials?.sessionId ?? 'none'] as const;
  /* Ведущий удалил участника: живое соединение закрыто, гостевой доступ не действует. */
  const [removed, setRemoved] = useState(false);
  const connected = useRef(false);
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: () => fetchGuestSpreadSession(credentials!),
    enabled: Boolean(credentials && invitation.data?.kind === 'spread') && !removed,
    retry: false,
    /*
      Тест 16 сентября: ведущий включил свободный порядок, а вкладки участников
      продолжали писать «Сейчас тянет Альбина». Сервер никого не запрещал —
      страница не узнала о смене: фоновую вкладку браузер усыпил, и снимок до
      неё не дошёл. Поэтому страница сверяется с сервером, когда на неё
      возвращаются, и сама опрашивает сервер, пока живого соединения нет.
    */
    refetchOnWindowFocus: 'always',
    refetchInterval: () => (connected.current ? false : 10_000),
    /* Ни перезапрос, ни опрос не откатывают стол к состоянию старше показанного. */
    structuralSharing: keepNewestSession,
  });
  const realtimeStatus = useSessionRealtime<SpreadSessionView>({
    sessionId: credentials?.sessionId ?? '',
    guestToken: credentials?.guestToken,
    enabled: Boolean(credentials && invitation.data?.kind === 'spread') && !removed,
    onSnapshot: (snapshot) => applySession(queryClient, sessionKey, snapshot),
    onRemoved: () => {
      /* Удалённый не должен вернуться прежним участником, открыв ссылку снова. */
      forgetGuestCredentials(token);
      setRemoved(true);
    },
  });
  connected.current = realtimeStatus === 'connected';

  /** Забыть участника этого приглашения и вернуть форму имени. */
  const leave = (reason: string | null) => {
    forgetGuestCredentials(token);
    if (credentials) queryClient.removeQueries({ queryKey: ['guest-spread-session', credentials.sessionId] });
    setCredentials(null);
    setRemoved(false);
    setNotice(reason);
    void invitation.refetch();
  };

  /*
    Запомненный участник больше не действует: ведущий удалил его, пока вкладка
    спала, или сессии нет. Данные стираются только на такой ответ сервера — при
    обрыве связи участник остаётся прежним и дождётся её возвращения.
  */
  const revoked = session.isError && isRevokedGuestAccess(session.error);
  useEffect(() => {
    if (revoked) leave('Прежнее участие в этой сессии больше не действует. Войдите заново.');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на смену признака
  }, [revoked]);

  const form = useForm({
    initialValues: { displayName: '' },
    validate: {
      displayName: (value) => value.trim().length < 2 ? 'Введите не меньше двух символов' : null,
    },
  });
  const join = useMutation({
    mutationFn: (displayName: string) => joinSpreadSession(token, { displayName }),
    onSuccess: (result) => {
      saveGuestCredentials(token, result.credentials);
      setNotice(null);
      setCredentials(result.credentials);
      applySession(queryClient, ['guest-spread-session', result.credentials.sessionId], result.session);
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  const commit = (snapshot: SpreadSessionView) => applySession(queryClient, sessionKey, snapshot);
  const fail = (error: unknown) => notifications.show({ color: 'red', message: toUserMessage(error) });
  const draw = useMutation({
    mutationFn: (input: DrawSpreadCardInput) => drawGuestSpreadCard(credentials!, input),
    onSuccess: commit,
    onError: fail,
  });
  const move = useMutation({
    mutationFn: ({ cardId, input }: { cardId: string; input: MoveSpreadCardInput }) =>
      moveGuestSpreadCard(credentials!, cardId, input),
    onSuccess: commit,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (cardId: string) => removeGuestSpreadCard(credentials!, cardId),
    onSuccess: commit,
    onError: fail,
  });

  if (invitation.isLoading) return <LoadingState label="Проверяем приглашение..." />;
  if (invitation.isError || !invitation.data) {
    return (
      <Container size="sm" py={80}>
        <ErrorState title="Приглашение недействительно" message="Попросите ведущего прислать новую ссылку." />
      </Container>
    );
  }

  if (invitation.data.kind === 'game') {
    return <GuestGameSession invitation={invitation.data} token={token} />;
  }

  if (!credentials) {
    /*
      SPR-14: комната открыта, пока её не закрыл ведущий или сама сессия. Старт
      расклада вход больше не закрывает — именно это на показе отсекало людей,
      которые подключались уже после начала.
    */
    const closed = !invitation.data.entryOpen;
    return (
      <GuestFrame>
        <Paper withBorder p="xl" radius="lg">
          <Stack gap="lg">
            <div>
              <Badge variant="light" color="teal" mb="sm">Групповая сессия</Badge>
              <Title order={1}>{invitation.data.title}</Title>
              <Text c="var(--text-muted)" mt="xs">
                {invitation.data.spread.description ?? invitation.data.spread.title}
              </Text>
              <Text size="sm" mt="sm">Ведущий: {invitation.data.hostDisplayName}</Text>
            </div>
            {notice ? (
              <Alert color="gold" icon={<IconAlertCircle size={18} />} data-testid="guest-notice">
                {notice}
              </Alert>
            ) : null}
            {closed ? (
              <Alert color="gold" icon={<IconAlertCircle size={18} />} title="Вход закрыт">
                {invitation.data.status === 'completed'
                  ? 'Эта сессия уже завершена.'
                  : 'Ведущий закрыл подключение к этой комнате.'}
              </Alert>
            ) : (
              <form onSubmit={form.onSubmit((values) => join.mutate(values.displayName.trim()))}>
                <Stack>
                  <TextInput
                    label="Как вас показать участникам"
                    placeholder="Ваше имя"
                    autoFocus
                    data-testid="guest-name"
                    {...form.getInputProps('displayName')}
                  />
                  <Button type="submit" loading={join.isPending} leftSection={<IconLogin2 size={17} />}>
                    Присоединиться
                  </Button>
                </Stack>
              </form>
            )}
          </Stack>
        </Paper>
      </GuestFrame>
    );
  }

  if (removed) {
    return (
      <GuestFrame>
        <Alert color="gold" icon={<IconAlertCircle size={18} />} title="Ведущий удалил вас из сессии">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              Если это ошибка, попросите ведущего пустить вас снова. Пока вход открыт, можно
              присоединиться заново — уже как новый участник.
            </Text>
            <Button size="xs" variant="light" onClick={() => leave(null)}>
              Войти заново
            </Button>
          </Stack>
        </Alert>
      </GuestFrame>
    );
  }

  if (session.isLoading || revoked) return <LoadingState label="Восстанавливаем групповую сессию..." />;
  if (session.isError || !session.data) {
    /*
      Сюда попадает только то, что не отказ в доступе: обрыв связи или сбой
      сервера. Участник не забывается — он тот же, просто сервер сейчас
      недоступен.
    */
    return (
      <GuestFrame>
        <Alert color="red" title="Нет связи с сервером">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              {toUserMessage(session.error)} Вы остаётесь участником «{credentials.displayName}» —
              после восстановления связи всё продолжится с того же места.
            </Text>
            <Button size="xs" variant="light" loading={session.isFetching} onClick={() => void session.refetch()}>
              Повторить
            </Button>
          </Stack>
        </Alert>
      </GuestFrame>
    );
  }

  const current = session.data;
  /* Перемещение карты не в счёт: стол показывает его сразу и сохраняет сам. */
  const busy = draw.isPending || remove.isPending;

  /*
    Очередь участников (§бэклог 9). Пока ведущий никого не назначил, тянут все.
    Назначил другого — стол остаётся видимым, но карту не вытянуть, и сказано,
    чьей очереди ждём: запертая без объяснения рубашка читается как поломка.
  */
  const turnHolder = current.activeParticipantId
    ? current.participants.find((participant) => participant.id === current.activeParticipantId)
    : null;
  const lockedReason =
    current.status === 'active' &&
    current.activeParticipantId &&
    current.activeParticipantId !== credentials.participantId
      ? `Сейчас тянет ${turnHolder?.displayName ?? 'другой участник'}. Дождитесь своей очереди.`
      : null;
  return (
    <GuestFrame wide>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text tt="uppercase" size="xs" fw={700} c="var(--accent)">Групповой расклад</Text>
            <Title order={1}>{current.spread.title}</Title>
            <Group gap="xs" mt="xs">
              <SessionStatusBadge status={current.status} />
              <Text size="sm" c="var(--text-muted)" data-testid="guest-display-name">{credentials.displayName}</Text>
              <SwitchGuestButton displayName={credentials.displayName} onConfirm={() => leave(null)} />
            </Group>
          </div>
          <Badge
            color={realtimeStatus === 'connected' ? 'teal' : 'gold'}
            leftSection={realtimeStatus === 'connected' ? <IconWifi size={13} /> : <IconWifiOff size={13} />}
          >
            {realtimeStatus === 'connected' ? 'В сети' : 'Переподключение'}
          </Badge>
        </Group>

        <SessionParticipants participants={current.participants} />

        {current.status === 'completed' ? (
          <Alert color="teal" title="Сессия завершена">
            Карты можно посмотреть ещё около часа, затем сессия удаляется.
          </Alert>
        ) : null}

        {/*
          После завершения стол остаётся на час (затем сессия удаляется): карты видно.
          Тянуть и двигать нельзя — стол сам запирается, когда сессия не активна.
        */}
        {current.status === 'waiting' ? (
          <Alert color="teal" title="Вы подключились">
            Ждём, когда ведущий начнёт сессию. Страница обновится автоматически.
          </Alert>
        ) : current.spread.layout === 'preset' ? (
          <>
            {/* Стол показывает свой расклад: позиции у каждого участника свои. */}
            <PresetSpread
              session={current}
              busy={busy}
              viewerParticipantId={credentials.participantId}
              viewedParticipantId={credentials.participantId}
              lockedReason={lockedReason}
              onDraw={(input) => draw.mutate(input)}
              fetchAvailableDecks={() => fetchGuestFreeformDecks(credentials)}
            />
            <SpreadCardsPanel
              session={current}
              viewerParticipantId={credentials.participantId}
            />
          </>
        ) : (
          <>
            <FreeformSpread
              key={current.id}
              session={current}
              busy={busy}
              lockedReason={lockedReason}
              onDraw={(input) => draw.mutate(input)}
              onMove={(cardId, input) => move.mutateAsync({ cardId, input })}
              onRemove={(cardId) => remove.mutate(cardId)}
              fetchAvailableDecks={() => fetchGuestFreeformDecks(credentials)}
            />
            {/* История нужна и на свободном столе: карт там набирается больше. */}
            <SpreadCardsPanel
              session={current}
              viewerParticipantId={credentials.participantId}
            />
          </>
        )}
      </Stack>
    </GuestFrame>
  );
}

function GuestFrame({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <Container size={wide ? 'xl' : 'sm'} py={{ base: 32, md: 64 }}>
      <Group gap="xs" mb="xl">
        <IconSun size={22} color="var(--accent)" />
        <Text ff="var(--font-serif)" fw={700} size="lg">{APP_NAME}</Text>
      </Group>
      {children}
    </Container>
  );
}
